/**
 * insurance-advance-paid.ts — what has been paid against one insurance advance.
 *
 * THE INSURANCE TAB PRINTED THIS FIGURE TWICE AND GOT TWO ANSWERS. Every policy card counts the
 * money three ways — the repayments recorded directly against the advance, the part of the weekly
 * payments the engine books to it, and, on a year the driver paid outright, the deposit that paid
 * it — while the "Repaid" card at the top of the tab left the weekly part out. So a driver whose
 * payments have been split read a headline smaller than the cards underneath it added up to, with
 * nothing on the screen to explain the difference. One advance, one figure, computed here.
 *
 * ── THE DEFECT IS OLDER THAN THE ADVANCES THAT MADE IT VISIBLE ───────────────
 * Splitting a weekly payment across the advances is not new, so the headline has been under-reading
 * for as long as any of these contracts have had one. What is new is that the engine now credits
 * the advances with NO weekly as well, once the vehicle is paid for [D-B58]: those advances move on
 * their allocations alone, so the gap is no longer a few cedis of rounding on an active year but the
 * whole of what has been recovered. The fix is the same either way, and it ships before the engine.
 *
 * ── WHY THE DEPOSIT IS ON ONE LIMB AND NOT THE OTHER ─────────────────────────
 * A year the driver paid IN FULL was paid by its deposit, and `repayments_total` counts
 * `insurance_loan_repayments` alone (`…_the_advance_says_what_it_was_for.sql:328`, disjoint from
 * the `insurance_loan_deposits` sum at `:329` — nothing is double counted by adding them) — so
 * without the deposit a wholly settled year reads "GHS 0.00" against a premium that is paid.
 * It joins the figure THERE AND NOWHERE ELSE: on a partly deposited advance this tab measures
 * against `amount - deposit_total`, so a deposit in the numerator would be money counted against a
 * total that never included it. On the paid-in-full branch the premium is taken gross on both
 * sides, which is what lets the percentage fall out at 100 with no special case.
 *
 * Fails closed on a payload that cannot answer: an unusable figure is nothing paid, which is the
 * reading that cannot tell a driver an advance is further along than it is.
 */

import { PremiumLoanShape, premiumPaidInFull } from './insurance-premium-shape';

/** An insurance advance's money, to the depth this reads. */
export interface InsuranceAdvanceMoneyShape extends PremiumLoanShape {
    /** `SUM(insurance_loan_repayments)` — what was paid straight at the advance. */
    repayments_total?: number | null;
    /** The allocator's share of the weekly payments booked to this advance. */
    allocated_total?: number | null;
}

/** A missing or unusable figure is nothing paid — see the docblock. */
function amountOf(value: number | null | undefined): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** What has been paid against this advance, counting every way the money can reach it. */
export function insuranceAdvancePaid(loan: InsuranceAdvanceMoneyShape | null | undefined): number {
    if (!loan) return 0;

    return (
        amountOf(loan.repayments_total) +
        amountOf(loan.allocated_total) +
        (premiumPaidInFull(loan) ? amountOf(loan.deposit_total) : 0)
    );
}
